import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import proj4 from 'proj4';

// MUI
import { Box, useMediaQuery, useTheme } from '@mui/material';

// OpenLayers
import { register as OlRegister } from 'ol/proj/proj4';
import OlMapBrowserEvent from 'ol/MapBrowserEvent';
import OlBaseLayer from 'ol/layer/Base';
import OlLayerGroup from 'ol/layer/Group';
import OlCollection from 'ol/Collection';
import OlSourceVector from 'ol/source/Vector';
import Geometry from 'ol/geom/Geometry';
import OlFeature from 'ol/Feature';
import { Style } from 'ol/style';

// Custom components
import { Controls, RotateControl, ScaleLineControl, ZoomControl, ZoomToExtentControl } from '@/components/Map/Controls';
import { Layers, VectorLayer } from '@/components/Map/Layers';
import GeoBaseLayerSwitcher from '@/components/Map/Controls/GEO/GeoBaseLayerSwitcher';
import GeoAPILayers from '@/components/Map/Layers/GEO/GeoAPILayers';
import { highlightedVectorStyle } from '@/components/Map/mapStyles';
import Map from '@/components/Map/Map';
import MapButton from '@/components/Map/Controls/Custom/MapButton';

// Context
import TenantContext, { TenantContextType } from '@/context/TenantContext/TenantContext';

// Lib
import { flattenLayers, padExtent } from '@/lib/olHelpers';
import DataController from '@/lib/DataController';

// Services
import mapService from '@/services/mapService';
import gsService from '@/services/gsService';

// Types
import { IModel } from '@/@types/models/model';
import { DCRecord } from '@/@types/lib/dataController';
import EnumLayerFunctionality from '@/@types/services/gsServiceEnums';
import { Record } from '@/@types/lib/api';

interface IDataCentricMiniMapProps {
  selectedPrimaryRecordId: number;
  onPrimaryRecordSelect: (id: number | null, featureProperties?: Record) => void;
  selectedSecondaryRecordId?: number;
  onSecondaryRecordSelect?: Function;

  model: IModel;
  secondaryModel?: IModel;
  mapId: number;
  allowHideObjects?: boolean;
  selectedLayerStyle?: (f: any) => Style[];

  replacePrimaryIdInSecondaryPath?: boolean;

  onResize?: () => void;
  isResized?: boolean;
  onGeomChange?: () => void;
  refreshMap?: boolean;
}

const DataCentricMiniMap = (props: IDataCentricMiniMapProps) => {
  const tenantContext = useContext(TenantContext) as TenantContextType;
  const { t } = useTranslation();

  const [mapInitialized, setMapInitialized] = useState(false);

  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  const { selectedPrimaryRecordId, onPrimaryRecordSelect, onResize, isResized } = props;
  const { model, mapId, selectedLayerStyle = highlightedVectorStyle, allowHideObjects } = props;
  const { selectedSecondaryRecordId, onSecondaryRecordSelect, secondaryModel, onGeomChange, refreshMap, replacePrimaryIdInSecondaryPath  } = props;

  const [recordExtent, setRecordExtent] = useState<number[]>([]);

  const defaultExtent = useMemo(
    () =>
      padExtent(
        tenantContext?.mapSettings?.default_extent
          ? tenantContext?.mapSettings?.default_extent
          : [1437016, 5271097, 2206278, 5860579]
      ),
    []
  );

  const viewOptions = useMemo(
    () => ({
      center: tenantContext?.mapSettings?.initial_view_center
        ? tenantContext.mapSettings.initial_view_center
        : ([1731757, 5581737] as [number, number]),
      extent: tenantContext?.mapSettings?.max_extent ? tenantContext.mapSettings.max_extent : [1437016, 5271097, 2206278, 5860579],
      zoom: tenantContext?.mapSettings?.initial_view_zoom ? tenantContext.mapSettings.initial_view_zoom : 8,
      minZoom: tenantContext?.mapSettings?.map_min_view_zoom ? tenantContext.mapSettings.map_min_view_zoom : 8,
      maxZoom: tenantContext?.mapSettings?.map_max_view_zoom ? tenantContext.mapSettings.map_max_view_zoom : 21,
    }),
    [tenantContext]
  );

  const [layersCollection, setLayersCollection] = useState<OlCollection<OlBaseLayer> | undefined>(undefined);
  const [selectedSource, setSelectedSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));

  const [showDOFLayer, setShowDOFLayer] = useState(true);
  const [showObjektiLayer, setShowObjektiLayer] = useState(true);

  const [hiddenLayerIDs, setHiddenLayerIDs] = useState<string[]>([]);

  // define proj
  proj4.defs(
    'EPSG:3765',
    '+proj=tmerc +lat_0=0 +lon_0=16.5 +k=0.9999 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  );
  proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');
  proj4.defs(
    'EPSG:31276',
    '+proj=tmerc +pm=greenwich +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=550.499,164.116,475.142,5.80967,2.07902,-11.62386,0.99999445824 +units=m +no_defs'
  );
  proj4.defs(
    'EPSG:3857',
    '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs'
  );
  OlRegister(proj4);

  const dc = new DataController(model);
  const dcSecondary = secondaryModel ? new DataController(secondaryModel) : undefined;

  const allowDOF = useMemo(() => {
    // if at least one layer has show_dof functionality allow the button
    if (layersCollection && layersCollection.getLength() > 0) {
      return layersCollection.getArray().some((layer) => layer.get('layer_functionality_id') === EnumLayerFunctionality.toggle_dof);
    }

    return false;
  }, [layersCollection]);

  useEffect(() => {
    getLayers();
  }, []);

  const getLayers = () => {
    mapService.getLayers(mapId).then((coll) => {
      setLayersCollection(coll);
      setMapInitialized(true);
    });
  };

  useEffect(() => {
    if (selectedPrimaryRecordId > 0) {
      dc.GetDataSingle(selectedPrimaryRecordId).then((resp) => {
        if (resp.success) {
          const dcRec = resp.data as DCRecord;
          const feat = dcRec?.geom as OlFeature;
          if (feat) {
            feat.set('type', 'primary');
            const srcVector = new OlSourceVector({ features: [feat] });
            // console.log("selecing primary after api call lmao lmao", selectedPrimaryRecordId)
            setSelectedSource(srcVector);
            const geom = feat.getGeometry();
            if (geom) {
              setRecordExtent(geom.getExtent());
            }
          }
        } else {
          setRecordExtent([]);
        }
      });
    } else {
      const srcVector = new OlSourceVector({ features: [] });
      setSelectedSource(srcVector);
      setRecordExtent([]);
      onPrimaryRecordSelect(null);
    }
    if (onGeomChange) onGeomChange();
  }, [selectedPrimaryRecordId, refreshMap]);

  useEffect(() => {
    if (!dcSecondary || !onSecondaryRecordSelect) return undefined;
    let cancelled = false;

    if (selectedSecondaryRecordId && selectedSecondaryRecordId > 0) {
      const path = replacePrimaryIdInSecondaryPath ? `${dcSecondary.apiPath.replace('{id}', selectedPrimaryRecordId.toString())}/${selectedSecondaryRecordId}` : undefined;
      dcSecondary.GetDataSingle(selectedSecondaryRecordId, path).then((resp) => {
        if (cancelled) return undefined;

        if (resp.success) {
          const dcRec = resp.data as DCRecord;
          const feat = dcRec?.geom as OlFeature;
          // console.log(dcRec)
          if (feat) {
            feat.set('type', 'secondary')
            const currentFeatures = selectedSource.getFeatures ? selectedSource.getFeatures() : [];
            const primaryFeature = currentFeatures.find(f => f.get('type') === 'primary');
            const newFeatures = primaryFeature ? [primaryFeature, feat] : [feat];
            const srcVector = new OlSourceVector({ features: newFeatures });
            setSelectedSource(srcVector);
            const geom = feat.getGeometry();
            if (geom) {
              setRecordExtent(geom.getExtent());
            }
          }
        } else {
          setRecordExtent([]);
        }
        return undefined;
      });
    } else {
      const currentFeatures = selectedSource.getFeatures ? selectedSource.getFeatures().filter(f => f.get('type') === 'primary') : [];
      const srcVector = new OlSourceVector({ features: currentFeatures });
      setSelectedSource(srcVector);
      setRecordExtent([]);
      onSecondaryRecordSelect(null);
    }

    return () => {
      cancelled = true;
    };
  }, [selectedSecondaryRecordId]);

  useEffect(() => {
    if (selectedSource) {
      const features = selectedSource.getFeatures();
      if (features.length > 0) {
        setRecordExtent(selectedSource.getExtent());
      }
    }
  }, [selectedSource]);

  const handleClick = (evt: OlMapBrowserEvent<any>) => {
    const feature = evt.map.forEachFeatureAtPixel(evt.pixel, (feat) => feat, {
      layerFilter: (layer) => {
        const layerId = layer.get('id');
        return layerId !== null && layerId !== undefined;
      },
    });

    if (feature) {
      const id = feature.getId();
      onPrimaryRecordSelect(id as number);
    } else {
      const allLayers = layersCollection ? flattenLayers(layersCollection.getArray()).filter((x) => !(x instanceof OlLayerGroup)) : [];
      // console.log(allLayers)
      const primaryRecordLayers = allLayers.filter((x) => {
        const layerName = x.get('layer');
        const callGroup = x.get('call_group');

        // finds the index of first underscore and slices everything after it
        const layerNameParsed = layerName.slice(layerName.indexOf('_') + 1);
        // console.log(layerNameParsed, model.layer, x.get('query'), )
        return x.get('query') === true && (layerNameParsed === model.layer || (model.layerCallGroup && model.layerCallGroup === callGroup));
      });

      if (primaryRecordLayers.length > 0) {
        gsService.getFeatureInfo(evt.map, evt.pixel, primaryRecordLayers).then((resp) => {
          if (resp && Object.keys(resp).length !== 0) {
            const layerKeys = Object.keys(resp);
            const key = layerKeys.length > 0 ? layerKeys[0] : null;
            const features = key ? resp[key] : [];

            if (Array.isArray(features) && features.length > 0) {
              const feat = features[0];
              const id = feat.properties[model.minimapGfiIdAttribute ?? "id"] as number | null;
              onPrimaryRecordSelect(id, feat.properties as DCRecord);
            } else {
              onPrimaryRecordSelect(null);
            }
          } else {
            onPrimaryRecordSelect(null);
          }
        });
      } else {
        onPrimaryRecordSelect(null);
      }
    }
  };

  const handleLayerSwitcherDOFClick = () => {
    setShowDOFLayer((prevState) => !prevState);
  };

  const handleLayerSwitcherObjektiClick = () => {
    setShowObjektiLayer((prevState) => !prevState);
  };

  useEffect(() => {
    const hiddenLayerIDsNew: string[] = [];
    if (!showDOFLayer) hiddenLayerIDsNew.push('dof');
    if (!showObjektiLayer) hiddenLayerIDsNew.push(model.layer ?? (model.layerCallGroup ?? 'objekti'));

    setHiddenLayerIDs(hiddenLayerIDsNew);
  }, [showDOFLayer, showObjektiLayer]);

  const handleClickResize = () => {
    if (onResize) {
      onResize();
    }
  };

  if (layersCollection)
    return (
      <Map
        height="500px"
        view={viewOptions}
        onClick={handleClick}
        className="sidebar-map"
        id="mini-map"
        zoomToExtent={recordExtent && recordExtent.length ? recordExtent : undefined}
        zoomToExtentPadding={isResized ? [150, 150, 150, 150] : [40, 40, 40, 40]}
        initialized={mapInitialized}
      >
        <Controls>
          <ZoomControl zoomInTipLabel={`${t('map:controls.zoom_in')}`} zoomOutTipLabel={`${t('map:controls.zoom_out')}`} />
          <RotateControl autoHide={false} />
          <ScaleLineControl type="minimap" />
          <GeoBaseLayerSwitcher
            type="minimap"
            mapId={mapId}
            allowNoLayer={false}
          />
          <ZoomToExtentControl
            id="zoom-extent-default"
            extent={defaultExtent}
            tipLabel={`${t('map:controls.zoom_to_extent')}`}
            className="ol-control ol-zoom-extent"
          />
          <ZoomToExtentControl
            id="zoom-extent-selected"
            extent={recordExtent}
            tipLabel={`${t('map:controls.zoom_to_selected')}`}
            className="ol-control ol-zoom-selected"
          />
          <Box
            sx={{
              position: 'relative',
            }}
          >
            {allowDOF && (
              <MapButton
                id="layerswitcher-dof"
                className="ol-layerswitcher-dof"
                handleClick={handleLayerSwitcherDOFClick}
                title={t('map:layerswitcher.toggle_dof') as string}
                active={showDOFLayer}
              >
                <i className="fas fa-camera" />
              </MapButton>
            )}
          {allowHideObjects && (
            <MapButton
              id="layerswitcher-objekti"
              className="ol-layerswitcher-objekti"
              handleClick={handleLayerSwitcherObjektiClick}
              title={t('map:layerswitcher.toggle_objekti') as string}
              active={showObjektiLayer}
            >
              <i className="fas fa-vector-square" />
            </MapButton>
          )}
            {onResize && mdUp ? (
              <MapButton id="button-resize" className="ol-button-resize" handleClick={handleClickResize}>
                <i className={isResized ? 'fas fa-angle-double-down' : 'fas fa-angle-double-up'} />
              </MapButton>
            ) : null}
          </Box>
        </Controls>
        <Layers>
          <GeoAPILayers layersCollection={layersCollection} hiddenIDs={hiddenLayerIDs} />
          <VectorLayer id="selected" source={selectedSource} style={selectedLayerStyle} zIndex={950} />
        </Layers>
      </Map>
    );

  return null;
};

export default DataCentricMiniMap;
